/**
 * 멀티테넌트 Firestore 경로 헬퍼
 *
 * 모든 학원 데이터는 `academies/{academyId}/` 하위에 격리된다.
 * 서비스·페이지는 컬렉션 경로를 직접 쓰지 말고 반드시 이 헬퍼를 통해 접근한다.
 * (경로가 한 곳에 모여 있어야 구조 변경·보안 검토가 쉬움)
 */
import { collection, doc } from 'firebase/firestore';
import { db } from './config';

/* ── 전역 (학원에 속하지 않음) ───────────────────────── */

// 사용자 (회원가입 단위) — name, phone, memberships[] 등
export const usersCol = () => collection(db, 'users');
export const userDoc = (uid) => doc(db, 'users', uid);

// 학원 (공개 프로필 = 디렉토리가 읽는 문서)
export const academiesCol = () => collection(db, 'academies');
export const academyDoc = (aid) => doc(db, 'academies', aid);

/* ── 학원 하위 (academies/{aid}/...) ─────────────────── */

// 구성원 (원장·강사·조교·학부모) + 권한
export const membersCol = (aid) => collection(db, 'academies', aid, 'members');
export const memberDoc = (aid, uid) => doc(db, 'academies', aid, 'members', uid);

// 학부모 가입 신청
export const joinRequestsCol = (aid) => collection(db, 'academies', aid, 'joinRequests');
export const joinRequestDoc = (aid, id) => doc(db, 'academies', aid, 'joinRequests', id);

// 수강료 수납 (무통장입금 확인)
export const paymentsCol = (aid) => collection(db, 'academies', aid, 'payments');
export const paymentDoc = (aid, id) => doc(db, 'academies', aid, 'payments', id);

// 학원 일정
export const academyEventsCol = (aid) => collection(db, 'academies', aid, 'academyEvents');
export const academyEventDoc = (aid, id) => doc(db, 'academies', aid, 'academyEvents', id);

// 학원 공지 (학원 전체 대상 — 클래스 공지와 별개)
export const academyNoticesCol = (aid) => collection(db, 'academies', aid, 'notices');
export const academyNoticeDoc = (aid, id) => doc(db, 'academies', aid, 'notices', id);

// 학원 자료실 (학원 전체 공유 — 클래스 자료와 별개)
export const academyMaterialsCol = (aid) => collection(db, 'academies', aid, 'academyMaterials');
export const academyMaterialDoc = (aid, id) => doc(db, 'academies', aid, 'academyMaterials', id);

// 학원 설정 (단일 문서)
export const settingsDoc = (aid) => doc(db, 'academies', aid, 'settings', 'main');

/* ── 클래스 및 그 하위 ───────────────────────────────── */

export const classesCol = (aid) => collection(db, 'academies', aid, 'classes');
export const classDoc = (aid, cid) => doc(db, 'academies', aid, 'classes', cid);

export const attendanceCol = (aid, cid) => collection(db, 'academies', aid, 'classes', cid, 'attendance');
export const attendanceDoc = (aid, cid, date) => doc(db, 'academies', aid, 'classes', cid, 'attendance', date);
export const gradesCol = (aid, cid) => collection(db, 'academies', aid, 'classes', cid, 'grades');
export const gradeDoc = (aid, cid, gid) => doc(db, 'academies', aid, 'classes', cid, 'grades', gid);
export const assignmentsCol = (aid, cid) => collection(db, 'academies', aid, 'classes', cid, 'assignments');
export const assignmentDoc = (aid, cid, asgId) => doc(db, 'academies', aid, 'classes', cid, 'assignments', asgId);
export const materialsCol = (aid, cid) => collection(db, 'academies', aid, 'classes', cid, 'materials');
export const materialDoc = (aid, cid, mid) => doc(db, 'academies', aid, 'classes', cid, 'materials', mid);
export const videosCol = (aid, cid) => collection(db, 'academies', aid, 'classes', cid, 'videos');
export const noticesCol = (aid, cid) => collection(db, 'academies', aid, 'classes', cid, 'notices');
export const noticeDoc = (aid, cid, nid) => doc(db, 'academies', aid, 'classes', cid, 'notices', nid);
export const logsCol = (aid, cid) => collection(db, 'academies', aid, 'classes', cid, 'logs');
export const logDoc = (aid, cid, lid) => doc(db, 'academies', aid, 'classes', cid, 'logs', lid);

/* ── 채팅 ────────────────────────────────────────────── */

export const chatRoomsCol = (aid) => collection(db, 'academies', aid, 'chatRooms');
export const chatRoomDoc = (aid, rid) => doc(db, 'academies', aid, 'chatRooms', rid);
export const messagesCol = (aid, rid) => collection(db, 'academies', aid, 'chatRooms', rid, 'messages');

/* ── MVP: 학원 직속 학생·출석 ────────────────────────── */

export const studentsCol = (aid) => collection(db, 'academies', aid, 'students');
export const studentDoc = (aid, sid) => doc(db, 'academies', aid, 'students', sid);
export const attendanceLogCol = (aid) => collection(db, 'academies', aid, 'attendanceLog');
export const attendanceLogDoc = (aid, date) => doc(db, 'academies', aid, 'attendanceLog', date);
